/* ══════════════════════════════════════════════════════════════════════
   Dynamic iridescent gradient — derived from the single anchor color
   the super-admin configures for the app (platform.wsDefaultAnchorColor).
   Same input that drives anchorDerivedColors() for the workspace palette,
   so the landing / signup / sign-in / splash gradients automatically
   harmonize with the workspace whenever the anchor changes.

   Implementation: monochromatic depth gradient within the anchor's hue
   family — a dark shade of the anchor at the start fading to a light
   shade of the same anchor at the end. Both stops come from the OKLab
   tonal palette of the anchor, so the hue stays constant and only the
   lightness (and chroma) varies across the strip.

   Earlier iterations used hue rotation to add iridescence but that
   shifted the gradient's second stop into a different color family
   (blue → green, violet → cyan, etc.) which felt untethered from the
   anchor. Monochromatic depth stays recognizably "the anchor color"
   end to end — just a darker-to-lighter wash.

   Two knobs:

   - STOP_START_DEPTH — lightness tier for the darker start stop
     (0 = near-black, 11 = near-white). Lower number = deeper start.
   - STOP_END_DEPTH — lightness tier for the lighter end stop. Higher
     number = airier end. Keep this above STOP_START_DEPTH.
   ══════════════════════════════════════════════════════════════════════ */

const STOP_START_DEPTH = 4;  /* ≈ L 0.33 — deep saturated start */
const STOP_END_DEPTH   = 7;  /* ≈ L 0.72 — airy soft end        */

/* Pick a hex at a given lightness tier from the anchor's OKLab tonal
   palette. Inlined from src/utils/color.js so this file stays
   self-contained and easy to tune in isolation. */
function paletteStop(anchor, index) {
  if (!anchor || typeof anchor !== "string" || anchor.length !== 7) return anchor;
  const ri = parseInt(anchor.slice(1, 3), 16) / 255;
  const gi = parseInt(anchor.slice(3, 5), 16) / 255;
  const bi = parseInt(anchor.slice(5, 7), 16) / 255;
  const lin = c => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const rl = lin(ri), gl = lin(gi), bl = lin(bi);
  const l_ = Math.cbrt(0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl);
  const m_ = Math.cbrt(0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl);
  const s_ = Math.cbrt(0.0883024619 * rl + 0.2024326073 * gl + 0.6892649308 * bl);
  const a0 = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const b0 = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
  const C0 = Math.sqrt(a0 * a0 + b0 * b0);
  const hueRad = Math.atan2(b0, a0);
  const STOPS = [0.04, 0.08, 0.14, 0.22, 0.33, 0.46, 0.59, 0.72, 0.82, 0.89, 0.95, 0.97];
  const L = STOPS[Math.max(0, Math.min(STOPS.length - 1, index))];
  const chromaScale = Math.max(0, 1 - Math.pow((L - 0.5) / 0.45, 2));
  const C = C0 * chromaScale;
  const l2 = L + 0.3963377774 * (C * Math.cos(hueRad)) + 0.2158037573 * (C * Math.sin(hueRad));
  const m2 = L - 0.1055613458 * (C * Math.cos(hueRad)) - 0.0638541728 * (C * Math.sin(hueRad));
  const s2 = L - 0.0894841775 * (C * Math.cos(hueRad)) - 1.2914855480 * (C * Math.sin(hueRad));
  const l3 = l2 * l2 * l2, m3 = m2 * m2 * m2, s3 = s2 * s2 * s2;
  let R = +4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
  let G = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
  let B = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.7076147010 * s3;
  R = Math.max(0, Math.min(1, R)); G = Math.max(0, Math.min(1, G)); B = Math.max(0, Math.min(1, B));
  const gam = c => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
  const rr = Math.round(gam(R) * 255), gg = Math.round(gam(G) * 255), bb = Math.round(gam(B) * 255);
  return `#${rr.toString(16).padStart(2, "0")}${gg.toString(16).padStart(2, "0")}${bb.toString(16).padStart(2, "0")}`;
}

/* Light-end companion color. Exposed for places that want just that
   hex (e.g., SVG <linearGradient> defs that can't read CSS vars). */
export function companionColor(anchor) {
  return paletteStop(anchor, STOP_END_DEPTH);
}

/* Build a CSS string that overrides --accent-a / --accent-b /
   --accent-grad at :root based on a single anchor hex. Inject this
   into a <style> tag at app root and every element in the app can
   read the anchor-derived tokens (including splash, modals, tooltips
   that live outside the .cmd-landing tree). */
export function buildAccentGradientCss(anchor) {
  if (!anchor || typeof anchor !== "string" || anchor.length !== 7) return "";
  const a = paletteStop(anchor, STOP_START_DEPTH);
  const b = paletteStop(anchor, STOP_END_DEPTH);
  return `:root{--accent-a:${a};--accent-b:${b};--accent-grad:linear-gradient(135deg,${a} 0%,${b} 100%);}`;
}



